/** @jsxImportSource @emotion/react */

import type { ComponentPropsWithoutRef, ElementType, FC, MutableRefObject, ReactNode } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import _uniqueId from 'lodash/uniqueId';
import { cx } from '@emotion/css';
import ODSGroup from '../ODSGroup';
import ODSFormFeedback from '../ODSFormFeedback';

type ODSInputProps =
  | Omit<ComponentPropsWithoutRef<'input' | 'select' | 'textarea'>, 'size' | 'prefix'> & {
      /** Every input field should have a label */
      label?: ReactNode;
      /** What something to appear before the input either a string or dropdown */
      prefix?: ReactNode;
      /** What something to appear after the input either a string or dropdown */
      suffix?: ReactNode;
      /** Help the user understand what to do */
      helpText?: ReactNode;
      /** Tell them why there's an error */
      errorMessage?: ReactNode;
      /** Is it good to go? Let em know */
      successMessage?: ReactNode;
      /** Show it's valid, makes the border green and shows the success message if any */
      valid?: boolean;
      /** If there's an error, makes the border red and shows the error message if any */
      invalid?: boolean;
      /** When in a ODSGrid how many columns should it be */
      columns?: number;
      /** Error/success message display as tooltip */
      tooltip?: boolean;
      /** Make textareas automatically set the height */
      autoResize?: boolean;
      /** Align the input text differently */
      textAlign?: 'left' | 'right' | 'center';
      /** No outline or rounded corners */
      asText?: boolean;
      /** Make the label allCaps aka little-heading */
      allCaps?: boolean;
      /** Display input inline */
      inline?: boolean;
      /** Set input field size */
      size?: 'sm' | 'md' | 'lg';
      /** Just for bootstrap parity, doesn't use */
      bsSize?: 'sm' | 'lg';
      /** Pass in custom error message id */
      errorId?: string;
      /** Pass in custom success message id */
      successId?: string;
      /** Ref to the input */
      forwardRef?: MutableRefObject<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | undefined
      >;
      /** Set the max number of rows for a textarea, useful with `autoResize` to stop it expanding forever */
      maxRows?: number;
      /** Render as plaintext input */
      plaintext?: boolean;
      /** Render as addon */
      addon?: boolean;
      /** Tag to render input as */
      tag?: ElementType;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Temporarily matching reactstrap types for tag
      [key: string]: any;
    };

interface TextProps {
  asText?: boolean;
  children?: React.ReactNode;
}

const InputText: FC<TextProps> = ({ asText, children }) => (
  <span
    className={cx(
      'd-flex align-items-center align-self-stretch text-secondary small user-select-none px-2 text-nowrap',
      { border: !asText, 'opacity-75': asText }
    )}
    css={(theme) => ({
      ...(!asText && { backgroundColor: theme.rgba(theme.colors.border, 0.5) }),
    })}
  >
    {children}
  </span>
);

const useAutoResizeTextarea = (
  ref: ODSInputProps['forwardRef'],
  value: string | undefined,
  maxRows?: number
) => {
  const borderRef = useRef(0);
  const maxHRef = useRef<number>(undefined);

  const resize = useCallback(() => {
    const el = ref?.current;
    if (!el) return;

    el.style.height = 'auto';
    const next = el.scrollHeight + borderRef.current;
    el.style.height = `${Math.min(next, maxHRef.current ?? next)}px`;
  }, [ref]);

  useLayoutEffect(() => {
    const el = ref?.current;
    if (!el) return;

    const styles = getComputedStyle(el);
    borderRef.current =
      (parseFloat(styles.borderTopWidth) || 0) + (parseFloat(styles.borderBottomWidth) || 0);

    if (maxRows) {
      const rowHeight = parseFloat(styles.lineHeight) || 18;
      const padding =
        (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
      maxHRef.current = rowHeight * maxRows + padding + borderRef.current;
      el.style.maxHeight = `${maxHRef.current}px`;
    } else {
      maxHRef.current = undefined;
      el.style.removeProperty('max-height');
    }

    resize();
    el.addEventListener('input', resize);

    // eslint-disable-next-line consistent-return
    return () => {
      el.removeEventListener('input', resize);
    };
  }, [resize, maxRows, ref]);

  useLayoutEffect(() => {
    resize();
  }, [value, resize]);
};

/**
 * An input to be used in forms, including helper text, error text, success text, and label.
 * Can be used as composable primitive to render other input type components with consistent
 * labels and other form control decorations.
 *
 * NOTE: Using textarea or select for the type prop is considered deprecated. Please use tag prop instead.
 * */
const ODSInput: FC<ODSInputProps> = ({
  className,
  label,
  invalid,
  valid,
  helpText,
  errorMessage,
  successMessage,
  prefix,
  suffix,
  columns,
  id,
  tooltip,
  autoResize,
  textAlign = 'left',
  asText,
  allCaps,
  inline,
  bsSize,
  size = bsSize ?? 'sm',
  errorId,
  successId,
  forwardRef,
  maxRows,
  plaintext,
  addon,
  tag,
  type = 'text',
  ...componentProps
}) => {
  const [uid] = useState(_uniqueId('odsinput-'));
  const innerRef = useRef(undefined);
  const ref = forwardRef ?? innerRef;
  const inputID = id ?? uid;

  useAutoResizeTextarea(
    autoResize ? ref : undefined,
    componentProps.value as string | undefined,
    maxRows
  );

  const checkInput = ['switch', 'radio', 'checkbox'].includes(type ?? '');

  // TODO: Deprecate and remove textarea/select types in favor of consistently using tag
  const textareaInput = type === 'textarea' || tag === 'textarea';
  const selectInput = type === 'select' || tag === 'select';
  const rangeInput = type === 'range';
  let Tag = tag ?? ((selectInput || textareaInput ? type : 'input') as ElementType);

  let formControlClass: string | null = 'form-control';

  if (plaintext) {
    formControlClass = `${formControlClass}-plaintext`;
    Tag ??= 'input';
  } else if (rangeInput) {
    formControlClass = 'form-range';
  } else if (selectInput) {
    formControlClass = 'form-select';
  } else if (checkInput) {
    if (addon) {
      formControlClass = null;
    } else {
      formControlClass = 'form-check-input';
    }
  }

  if (Tag === 'input' || (Tag && typeof Tag === 'function')) {
    componentProps.type = type === 'switch' ? 'checkbox' : type;
  }

  const inputField = (
    <Tag
      {...componentProps}
      ref={ref}
      aria-describedby={cx({
        [successId ?? `${inputID}-success`]: !invalid && valid && !!successMessage,
        [errorId ?? `${inputID}-error`]: invalid && !!errorMessage,
        [`${inputID}-help`]: !!helpText,
      })}
      id={inputID}
      className={cx(
        'w-100 position-relative tabular-nums',
        formControlClass,
        size && (selectInput ? `form-select-${size}` : `form-control-${size}`),
        {
          'styled-select': Tag === 'select',
          'as-text': asText,
          'is-invalid': invalid,
          'is-valid': valid,
        }
      )}
      css={{
        zIndex: 2,
        '&:focus, &:active': {
          zIndex: 3,
        },
        ...(autoResize && Tag === 'textarea' && { resize: 'none' }),
        textAlign,
        ...(textAlign === 'center' && { padding: 0 }),
      }}
      aria-invalid={invalid}
      {...(autoResize && Tag === 'textarea' && { resize: 'none' })}
    />
  );

  const fieldGroup =
    !!prefix || !!suffix ? (
      <ODSGroup segmented {...(asText && { round: 'none' })} className="w-100">
        {prefix && typeof prefix === 'string' ? (
          <InputText asText={asText}>{prefix}</InputText>
        ) : (
          prefix
        )}
        {inputField}
        {suffix && typeof suffix === 'string' ? (
          <InputText asText={asText}>{suffix}</InputText>
        ) : (
          suffix
        )}
      </ODSGroup>
    ) : (
      inputField
    );

  return (
    <ODSGroup
      {...(columns && { columns })}
      spacing="sm"
      stacked
      className={cx('ods-input-group position-relative', className)}
      inline={inline}
    >
      {label && (
        <label
          htmlFor={inputID}
          className={cx('text-secondary user-select-none', allCaps ? 'little-heading' : 'small')}
        >
          {label}
        </label>
      )}
      {fieldGroup}
      {!invalid && valid && successMessage && (
        <ODSFormFeedback valid tooltip={tooltip} show id={successId ?? `${inputID}-success`}>
          {successMessage}
        </ODSFormFeedback>
      )}
      {invalid && errorMessage && (
        <ODSFormFeedback valid={false} tooltip={tooltip} show id={errorId ?? `${inputID}-error`}>
          {errorMessage}
        </ODSFormFeedback>
      )}
      {helpText && (
        <div className="text-secondary small user-select-none" id={`${inputID}-help`}>
          {helpText}
        </div>
      )}
    </ODSGroup>
  );
};

export type { ODSInputProps };
export default ODSInput;
